/**
 * Scorer Health — Durable Object RPC observability.
 * Reads from /api/do-health (separate AE health dataset).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, Badge } from "@/components/primitives";
import { fmtNumber, fmtPct, fmtLatency } from "@/lib/format";

const P95_WARN_MS = 100;
const ERROR_WARN_RATE = 0.005;

export function ScorerHealth() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["doHealth", filters, tick],
    queryFn: ({ signal }) => api.doHealth(filters, signal),
  });

  if (q.isLoading) return <Skeleton className="h-96" />;
  if (q.error) return <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  if (!q.data?.data) return <Card><EmptyState title="No DO health data." /></Card>;

  const { perClass, hotKeys, missingFeatureRate } = q.data.data;
  const warnings = perClass.filter((r) => r.p95Ms > P95_WARN_MS || r.errorRate > ERROR_WARN_RATE);

  return (
    <div className="flex flex-col gap-4">
      {warnings.length > 0 && (
        <Card className="border-sev-high/50">
          <CardHeader
            title="Health warnings"
            subtitle={`${warnings.length} class${warnings.length === 1 ? "" : "es"} exceed p95 ${P95_WARN_MS} ms or error rate ${(ERROR_WARN_RATE * 100).toFixed(2)}%`}
            action={<Badge tone="high">attention</Badge>}
          />
          <ul className="space-y-1 text-xs text-fg-muted">
            {warnings.map((w) => (
              <li key={w.doClass} className="flex items-center justify-between gap-2 rounded border border-border bg-surface-2 px-2 py-1">
                <span className="font-mono text-fg">{w.doClass}</span>
                <span>p95 <span className="font-mono text-fg">{fmtLatency(w.p95Ms)}</span></span>
                <span>errors <span className="font-mono text-fg">{fmtPct(w.errorRate, 2)}</span></span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="RPC latency by Durable Object class" subtitle="Weighted percentiles over the selected window. p95 above 100 ms or error rate above 0.5% is worth a look." />
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Class</th>
                  <th className="px-3 py-2 text-right font-medium">Calls</th>
                  <th className="px-3 py-2 text-right font-medium">p50</th>
                  <th className="px-3 py-2 text-right font-medium">p95</th>
                  <th className="px-3 py-2 text-right font-medium">p99</th>
                  <th className="px-3 py-2 text-right font-medium">Errors</th>
                  <th className="px-3 py-2 text-right font-medium">Timeouts</th>
                </tr>
              </thead>
              <tbody>
                {perClass.map((r) => (
                  <tr key={r.doClass} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-fg">{r.doClass}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg">{fmtNumber(r.callCount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg-muted">{fmtLatency(r.p50Ms)}</td>
                    <td className={"px-3 py-2 text-right font-mono " + (r.p95Ms > P95_WARN_MS ? "text-sev-high" : "text-fg-muted")}>{fmtLatency(r.p95Ms)}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg-muted">{fmtLatency(r.p99Ms)}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge tone={r.errorRate > 0.01 ? "high" : r.errorRate > 0.001 ? "watch" : "good"}>
                        {fmtPct(r.errorRate, 2)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Badge tone={r.timeoutRate > 0.005 ? "high" : "good"}>{fmtPct(r.timeoutRate, 2)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <CardHeader title="Hot Durable-Object keys" subtitle="Top 10 individual DO ids in the window. Hot keys can indicate a single attacker or a noisy entity skewing scoring." />
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Class</th>
                  <th className="px-3 py-2 text-left font-medium">Key</th>
                  <th className="px-3 py-2 text-right font-medium">Calls</th>
                  <th className="px-3 py-2 text-right font-medium">p95 latency</th>
                </tr>
              </thead>
              <tbody>
                {hotKeys.map((k) => (
                  <tr key={k.doClass + k.doId} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-fg">{k.doClass}</td>
                    <td className="px-3 py-2 font-mono text-fg-muted">{k.doId}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg">{fmtNumber(k.callCount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg-muted">{fmtLatency(k.avgLatencyMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {missingFeatureRate && (
        <Card>
          <CardHeader title="Missing-feature rates" subtitle="Share of scored events where each input feature was empty. High values mean the scorer ran without that signal." />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FeatureStat label="Bot score" value={missingFeatureRate.botScore} />
            <FeatureStat label="Threat score" value={missingFeatureRate.threatScore} />
            <FeatureStat label="JA4" value={missingFeatureRate.ja4} />
          </div>
        </Card>
      )}

      <ThreatListLiftTile />
    </div>
  );
}

/**
 * ThreatListLiftTile — for each Cloudflare managed list seen, the
 * share of hits that ended up high-risk. High lift = the list is a
 * reliable signal on this surface; low lift = the list is noisy and
 * may be over-weighted in the scorer.
 */
function ThreatListLiftTile() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["threat-list-lift", filters, tick],
    queryFn: ({ signal }) => api.threatListLift(filters, signal),
  });

  const rows = q.data?.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Threat-list lift"
        subtitle="For each Cloudflare managed list, the share of hits that ended up high-risk. Low lift = the list fires noisily on this surface."
      />
      {q.isLoading ? (
        <Skeleton className="h-32" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No managed-list hits in this window." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">List</th>
                <th className="px-3 py-2 text-right font-medium">High-risk hits</th>
                <th className="px-3 py-2 text-right font-medium">Total hits</th>
                <th className="px-3 py-2 text-right font-medium">Lift</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = r.liftPct * 100;
                const tone = pct >= 50 ? "high" : pct >= 20 ? "suspicious" : pct >= 5 ? "watch" : "good";
                return (
                  <tr key={r.list} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-fg">{r.list}</td>
                    <td className="px-3 py-2 text-right font-mono text-sev-high">{fmtNumber(r.highRisk)}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg-muted">{fmtNumber(r.total)}</td>
                    <td className="px-3 py-2 text-right"><Badge tone={tone}>{fmtPct(r.liftPct, 1)}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function FeatureStat({ label, value }: { label: string; value: number }) {
  const pct = value * 100;
  const tone = pct > 25 ? "high" : pct > 10 ? "suspicious" : pct > 2 ? "watch" : "good";
  return (
    <div className="rounded border border-border bg-surface-2 px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-fg-subtle">{label}</div>
        <Badge tone={tone}>{fmtPct(pct, 1)}</Badge>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
        <div className={"h-full rounded-full " + (tone === "high" ? "bg-sev-high" : tone === "suspicious" ? "bg-sev-suspicious" : tone === "watch" ? "bg-sev-watch" : "bg-sev-good")} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}
